import React from 'react';
import { useSpring } from '@react-spring/web';
import NextImage, { StaticImageData } from 'next/image';

import ArrowLink, { ArrowLinkProps } from 'Elements/Link/Arrow';
import theme from 'Theme/lightTheme';

import { Pindrop, Walking, CalendarCircled } from '@solent-university/solent-icons';

import {
    Container,
    ImageLink,
    ImageWrapperDefault,
    ImageWrapperMobile,
    Content,
    Info,
    Heading,
    StyledLink,
    Location,
    LocationItem,
    StyledIconLocation,
    LocationText,
    StyledIcon,
    Text,
    FeatureList,
    FeatureItem,
    FooterInfo,
    FooterInfoTablet,
    PriceWrapper,
    PriceContent,
    Price,
    FooterIcon
} from './Accommodation.styled';

export interface Props {
    heading: string;
    image: {
        default: {
            src: StaticImageData | string;
        };
        mobile: {
            src: StaticImageData | string;
        };
    };
    address: string;
    distance: string;
    text: string;
    price: string;
    featureList: { icon: React.ElementType; description: string }[];
    link: ArrowLinkProps;
}

const Accommodation: React.FC<Props> = props => {
    const { image, heading, address, distance, text, price, featureList, link } = props;
    const [isHovering, setIsHovering] = React.useState(false);

    const imageSpring = useSpring({
        transform: isHovering ? 'scale(1.2)' : 'scale(1)'
    });

    return (
        <>
            <Container>
                <ImageLink
                    href={link.href}
                    aria-label={link.label}
                    data-hover={isHovering}
                    onMouseOver={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                >
                    <ImageWrapperDefault style={imageSpring}>
                        <NextImage {...image.default} alt="" fill style={{ objectFit: 'cover' }} />
                    </ImageWrapperDefault>
                    <ImageWrapperMobile style={imageSpring}>
                        <NextImage {...image.mobile} alt="" fill style={{ objectFit: 'cover' }} />
                    </ImageWrapperMobile>
                </ImageLink>
                <Content>
                    <Info>
                        <Heading>
                            <StyledLink href={link.href} ariaLabel={link.ariaLabel} tabIndex={-1}>
                                {heading}
                            </StyledLink>
                        </Heading>
                        <Location>
                            <LocationItem>
                                <StyledIconLocation as={Pindrop} />
                                <LocationText>{address}</LocationText>
                            </LocationItem>
                            <LocationItem>
                                <StyledIconLocation as={Walking} />
                                <LocationText>{distance}</LocationText>
                            </LocationItem>
                        </Location>
                        <Text>{text}</Text>
                        <FeatureList>
                            {featureList.map((feature, index) => (
                                <FeatureItem key={index}>
                                    <StyledIcon as={feature.icon} />
                                    {feature.description}
                                </FeatureItem>
                            ))}
                        </FeatureList>
                    </Info>
                    <FooterInfo>
                        <PriceContent>
                            <FooterIcon as={CalendarCircled} />
                            <PriceWrapper>
                                <span>Starting from</span> <Price>{price} per week</Price>
                            </PriceWrapper>
                        </PriceContent>
                        <ArrowLink
                            variant="text"
                            iconColor={theme.colors.secondary.tints[2].hex}
                            {...link}
                        />
                    </FooterInfo>
                </Content>
            </Container>
            <FooterInfoTablet>
                <PriceContent>
                    <FooterIcon as={CalendarCircled} />
                    <PriceWrapper>
                        <span>Starting from</span> <Price>{price} per week</Price>
                    </PriceWrapper>
                </PriceContent>
                <ArrowLink
                    variant="text"
                    iconColor={theme.colors.secondary.tints[2].hex}
                    {...link}
                />
            </FooterInfoTablet>
        </>
    );
};

export default Accommodation;
